import { useState } from "react";
import { json, redirect, type MetaFunction, type LoaderFunctionArgs } from "@remix-run/node";
import { Link, useParams, useRouteError, isRouteErrorResponse } from "@remix-run/react";
import { QueryClient, dehydrate, useQuery } from "@tanstack/react-query";
import { getImageUrl, WORKSPACE_DETAIL_IMAGE_SIZE } from '~/utils/misc';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "~/components/ui/sheet";
import {
  Card,
  CardContent,
  CardHeader,
} from "~/components/ui/card";
import { Button } from "~/components/ui/button";
import { Separator } from "~/components/ui/separator";
import { Badge } from "~/components/ui/badge";
import {
  MapPin,
  Users2,
  // Share2,
  // Heart,
  Building2,
  MessageCircle,
  Pencil,
  HelpCircle,
} from "lucide-react";
import { trpcServer } from "~/common/trcp";
import { workspaceCanonicalPath } from "~/utils/workspace-path";
import { WorkspaceWithLocalizedContent } from "~/.server/api/routers/workspace";
import { WorkspaceBreadcrumb } from "~/components/breadcrumb";
import { ContactForm } from "~/components/contactForm";
import WorkspaceCarousel from "~/components/imageCarousel";
import Map from "~/components/map";
import { useMediaQuery } from "~/utils/hooks/useMediaQuery";
import { ShareDialog } from "~/components/shareDialog";
import { AmenityList } from "~/components/amenityList";
import { WorkspaceRentalTypesDisplay } from "~/components/workspaceRentalTypeDisplay";
import { useUser } from "~/utils/hooks/useUser";
import { useRouteLoaderData } from "@remix-run/react";
import { urlPattern } from "~/utils/urlPattern";
import { WhatsappChatButton } from "~/components/whatsappChatButton";

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
  if (!data?.dehydratedState) return [];
  // Get the workspace data from the reconstructed client
  const workspace = data.dehydratedState.queries?.[0]?.state?.data

  if (!isWorkspace(workspace)) return [];

  const title = `${workspace.title} | ${workspace.city || ''} ${workspace.category ? `| ${workspace.category}` : ''}`;
  const description = workspace.content.substring(0, 160);
  const imageSlug = workspace.images[0]?.slug;
  const image = getImageUrl(imageSlug, WORKSPACE_DETAIL_IMAGE_SIZE);

  // Determine image MIME type from extension
  const getImageMimeType = (filename?: string): string | null => {
    if (!filename) return null;
    const ext = filename.split('.').pop()?.toLowerCase();
    const mimeTypes: Record<string, string> = {
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      webp: 'image/webp',
      gif: 'image/gif',
    };
    return mimeTypes[ext || ''] || null;
  };
  const imageMimeType = getImageMimeType(imageSlug);

  // Use the actual route params for canonical URL to ensure it matches the current URL
  const citySlug = params.city || workspace.citySlug || 'all';
  const categorySlug = params.category || workspace.category || 'all';
  const workspaceSlug = params.workspaceSlug || workspace.slug;
  const canonicalUrl = `https://www.compartirespacios.com/espacios/${citySlug}/${categorySlug}/${workspaceSlug}`;

  return [
    { title },
    { name: "description", content: description },
    { property: "og:url", content: canonicalUrl },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:site_name", content: "Compartir Espacios" },
    { property: "og:image", content: image },
    { property: "og:image:width", content: "1220" },
    { property: "og:image:height", content: "780" },
    ...(imageMimeType ? [{ property: "og:image:type", content: imageMimeType }] : []),
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: image },
    { tagName: "link", rel: "canonical", href: canonicalUrl },
  ];
};

export async function loader({ request, params }: LoaderFunctionArgs) {
  const { workspaceSlug } = params;

  if (!workspaceSlug) {
    throw json({ message: "Not Found" }, { status: 404 });
  }

  const queryClient = new QueryClient();

  let workspaceData: WorkspaceWithLocalizedContent | null = null;

  try {
    workspaceData = await queryClient.fetchQuery({
      queryKey: ['workspace', workspaceSlug],
      queryFn: async () => await trpcServer(request).workspace.getWorkspace.query({
        slug: workspaceSlug,
        language: 'es'
      })
    });
  } catch (error) {
    if (error && typeof error === 'object' && 'data' in error) {
      const trpcError = error as { data?: { code?: string } };
      if (trpcError.data?.code === 'NOT_FOUND') {
        // The slug may be a retired one (the listing's slug changed):
        // 301 to its current URL instead of 404ing an indexed page.
        const target = await trpcServer(request).workspace.resolveSlugRedirect.query({
          slug: workspaceSlug,
        });
        if (target) {
          throw redirect(workspaceCanonicalPath(target), { status: 301 });
        }
        throw json({ message: "Workspace not found" }, { status: 404 });
      }
    }
    throw error;
  }

  if (workspaceData?.id) {
    await queryClient.prefetchQuery({
      queryKey: ['similar-workspaces', workspaceData.id],
      queryFn: () => trpcServer(request).workspace.getSimilarWorkspaces.query({
        workspaceId: workspaceData!.id,
      }),
    });
  }

  return ({
    dehydratedState: dehydrate(queryClient)
  });
}

function isWorkspace(data: unknown): data is WorkspaceWithLocalizedContent {
  const workspace = data as Partial<WorkspaceWithLocalizedContent>;
  return !!(
    workspace &&
    typeof workspace === 'object' &&
    workspace.id &&
    workspace.slug &&
    workspace.title &&
    workspace.content
  );
}

const workspaceKeys = {
  all: ['workspace'] as const,
  detail: (slug: string) => [...workspaceKeys.all, slug] as const,
};

function WorkspaceCallToActions() {
  const { category, city } = useParams();
  const formattedCity = city ? city.charAt(0).toUpperCase() + city.slice(1) : '';
  const categoryMapping: Record<string, string> = {
    'oficina': 'oficinas',
    'taller': 'talleres',
    'estudio': 'estudios',
    'nave': 'naves',
    'sala-de-yoga': 'salas de yoga',
    'coworking': 'coworkings'
    // Add other mappings as needed
  };
  
  const pluralCategory = category ? (categoryMapping[category] || `espacios de ${category}`) : 'espacios';

  return (
    <Card className="mb-6">
      <CardContent className="p-6 space-y-5">
        <div className="flex items-start gap-3 border-b pb-4">
          <MapPin className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-base">
              Para ver los mejores {pluralCategory} en {formattedCity || 'las principales ciudades'} pincha aquí.
            </h3>
            <Link
              to={urlPattern.build({ city, category })}
              className="text-blue-600 underline hover:text-blue-800 mt-1 inline-block text-sm"
            >
              Ver todos los {pluralCategory} →
            </Link>
          </div>
        </div>
        
        <div className="flex items-start gap-3 border-b pb-4">
          <Building2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-base">
              ¿Quieres alquilar tu oficina, estudio, consulta, nave, sala de yoga o cualquier tipo de espacio?
            </h3>
            <Link
              to="/cuenta/espacios"
              className="text-blue-600 underline hover:text-blue-800 mt-1 inline-block text-sm"
            >
              Publica tu espacio →
            </Link>
          </div>
        </div>
        
        <div className="flex items-start gap-3">
          <HelpCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-base">
              ¿No encuentras lo que buscas? Solicita un espacio y lo buscamos por ti.
            </h3>
            <Link
              to="/solicitar-espacio"
              className="text-blue-600 underline hover:text-blue-800 mt-1 inline-block text-sm"
            >
              Solicitar un espacio →
            </Link>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Content({ content, maxLength = 1000 }: { content: string; maxLength?: number }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const paragraphs = content.split('\n');
  
  // Function to truncate text while preserving paragraphs
  const truncateContent = (paragraphs: string[]): string[] => {
    let currentLength = 0;
    const truncatedParagraphs: string[] = [];
    
    for (const paragraph of paragraphs) {
      if (currentLength + paragraph.length <= maxLength) {
        truncatedParagraphs.push(paragraph);
        currentLength += paragraph.length;
      } else {
        // For the last visible paragraph, truncate it
        const remainingLength = maxLength - currentLength;
        if (remainingLength > 0) {
          truncatedParagraphs.push(paragraph.substring(0, remainingLength) + '...');
        }
        break;
      }
    }
    
    return truncatedParagraphs;
  };

  const shouldTruncate = content.length > maxLength;
  const visibleParagraphs = !shouldTruncate || isExpanded 
    ? paragraphs 
    : truncateContent(paragraphs);

  return (
    <>
      {/* Hidden full content for SEO */}
      {shouldTruncate && !isExpanded && (
        <div className="hidden">
          {paragraphs.map((paragraph, index) => (
            <p key={`seo-${index}`}>{paragraph}</p>
          ))}
        </div>
      )}
      
      {/* Visible content */}
      <div className="prose max-w-none">
        {visibleParagraphs.map((paragraph, index) => (
          <p key={index} className="mb-4 last:mb-0">
            {paragraph}
          </p>
        ))}
        
        {shouldTruncate && (
          <Button
            variant="link"
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-2 p-0"
          >
            {isExpanded ? 'Ver menos' : 'Ver más'}
          </Button>
        )}
      </div>
    </>
  );
}

// Client-only component for structured data to avoid hydration issues
function WorkspaceStructuredData({ workspace, category, city }: { workspace: WorkspaceWithLocalizedContent, category?: string, city?: string }) {
  const citySlug = city || workspace.citySlug || 'all';
  const categorySlug = category || workspace.category || 'all';
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    name: workspace.title,
    description: workspace.content,
    url: `https://www.compartirespacios.com/espacios/${citySlug}/${categorySlug}/${workspace.slug}`,
    datePosted: workspace.createdAt,
    image: workspace.images.map(img => getImageUrl(img?.slug, WORKSPACE_DETAIL_IMAGE_SIZE)),
    address: {
      "@type": "PostalAddress",
      streetAddress: workspace.address || "",
      addressLocality: workspace.city || "",
      addressCountry: workspace.countryCode || "",
      ...(workspace.postalCode && { postalCode: workspace.postalCode }),
    },
    ...(workspace.priceMin || workspace.priceMax ? {
      price: {
        "@type": "PriceSpecification",
        price: workspace.priceMin?.toString() || workspace.priceMax?.toString() || "",
        priceCurrency: "EUR",
        unitText: "month",
      }
    } : {}),
    ...(workspace.amenities?.length ? {
      amenityFeature: workspace.amenities.map(amenity => ({
        "@type": "LocationFeatureSpecification",
        name: amenity,
      }))
    } : {})
  };
  
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
}

export default function WorkspaceDetail() {
  const { workspaceSlug, category, city } = useParams();
  const { data: workspace } = useQuery<WorkspaceWithLocalizedContent>({
    queryKey: ['workspace', workspaceSlug]
  });

  const isMobile = useMediaQuery("(max-width: 768px)");
  const [isContactOpen, setIsContactOpen] = useState(false);
  
  const { isAdmin } = useUser();
  // Hooks must run unconditionally — this sits ABOVE the `!workspace` early return.
  const rootData = useRouteLoaderData('root') as { waChat?: 'variant' | 'control' } | undefined;

  if (!workspace) {
    return null;
  }

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  // Deterministic (server === client) canonical url for the WhatsApp prefill — deliberately
  // NOT derived from `window.location.href` like `currentUrl` above. `currentUrl` is '' during
  // SSR, so a prefill built from it bakes the GENERIC message into the SSR'd href, and React
  // does not patch mismatched attributes on hydrated host elements, leaving the per-page
  // context prefill silently absent in production. Same literal-host convention this file
  // already uses in `meta()` above (`canonicalUrl` / og:url), so server and client agree.
  const workspaceUrl = `https://www.compartirespacios.com/espacios/${city || workspace.citySlug || 'all'}/${category || workspace.category || 'all'}/${workspace.slug}`;

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Add structured data component */}
      <WorkspaceStructuredData workspace={workspace} category={category} city={city} />
      {/* Context-aware WhatsApp entry point: prefills a message referencing this specific
          space (root.tsx skips its generic instance here so this is the only one). Rendered
          ONLY for the `waChatEntry` variant group — control keeps the web ChatWidget that
          root.tsx renders on every page (option 1: one chat door per visitor, never both). */}
      {rootData?.waChat === 'variant' ? (
        <WhatsappChatButton workspace={{ title: workspace.title, url: workspaceUrl }} />
      ) : null}
      <WorkspaceBreadcrumb title={workspace.title} />
      <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row md:items-center md:justify-between mb-6">
        <h1 className="text-2xl font-bold">{workspace.title}</h1>
        <div className="flex space-x-4">
          <ShareDialog 
            title={workspace.title}
            url={currentUrl}
          />
          {/* <Button variant="outline" size="sm">
            <Heart className="h-4 w-4 mr-2" />
            Guardar
          </Button> */}
           {isAdmin && (
            <Button variant="outline" size="sm" asChild>
              <Link to={`/admin/espacios/${workspace.id}`}>
                <Pencil className="h-4 w-4 mr-2" />
                Editar como admin
              </Link>
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      {/* Image Gallery */}
      {workspace.images.length > 0 && (
        <WorkspaceCarousel 
          images={workspace.images}
          title={workspace.title}
        />
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
        <div className="md:col-span-2">
          {/* Description */}
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-semibold leading-none tracking-tight">{workspace.title}</h2>
                <Badge variant="secondary">{workspace.category || 'Coworking'}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <Content content={workspace.content} />
            </CardContent>
          </Card>
          {/* Amenities */}
          <AmenityList
            amenities={workspace.amenities}
            className="mb-6"
          />

          <WorkspaceCallToActions />

        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <Map
              workspaces={[{
                id: workspace.id,
                title: workspace.title,
                slug: workspace.slug,
                image: workspace.images[0] || null,
                city: workspace.city,
                citySlug: workspace.citySlug || '',
                countryCode: workspace.countryCode,
                currency: workspace.currency,
                latitude: workspace.latitude,
                longitude: workspace.longitude,
                priceMin: workspace.rentalTypes[0]?.priceMin || null,
                category: workspace.category || '',
                isSubscribed: workspace.isSubscribed || false
              }]}
              height="h-48"
              zoom={15}
            />
            <CardContent className="p-6">
              <h3 className="font-semibold text-lg mb-4">Ubicación y disponibilidad</h3>
              <div className="space-y-4">
                {/* Location */}
                <div className="flex items-start gap-2">
                  <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="font-semibold">Ubicación</p>
                    <p className="text-sm text-muted-foreground">{workspace.address}</p>
                    <p className="text-sm text-muted-foreground">
                      {workspace.city}, {workspace.countryCode}
                    </p>
                  </div>
                </div>
                <WorkspaceRentalTypesDisplay
                  rentalTypes={workspace.rentalTypes.map(rt => ({
                    slug: rt.slug,
                    priceMin: rt.priceMin || 0,
                    priceMax: rt.priceMax || 0,
                    currency: rt.currency
                  }))}
                />
                {/* Price */}
   

                {/* Rental Types */}
    

                {/* Availability */}
                <div className="flex items-start gap-2">
                  <Users2 className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="font-semibold">Disponibilidad</p>
                    <p className="text-sm text-muted-foreground">Inmediata</p>
                  </div>
                </div>
              </div>

              <Separator className="my-6" />

              {/* Contact */}
              {
                isMobile ? (
                  <Sheet open={isContactOpen} onOpenChange={setIsContactOpen}>
                    <SheetTrigger asChild>
                      <Button 
                        size="lg"
                        className="fixed bottom-4 left-4 right-4 z-50 mx-auto max-w-md"
                      >
                        <MessageCircle className="mr-2 h-5 w-5" />
                        Contactar
                      </Button>
                    </SheetTrigger>
                    <SheetContent
                      side="bottom" 
                      className="h-[80vh] overflow-y-auto"
                    >
                      <SheetHeader>
                        <SheetTitle>Contactar con el propietario</SheetTitle>
                      </SheetHeader>
                      <div className="flex-1 overflow-y-auto -mx-6 px-6 pb-6">
                        <div className="mt-6">
                          <ContactForm workspaceId={workspace.id} isMobile category={category} />
                        </div>
                      </div>
                    </SheetContent>
                  </Sheet>
                ) : (
                  <div className="hidden md:block">
                    <ContactForm workspaceId={workspace.id} category={category} />
                  </div>
                )
              }

            
            </CardContent>
          </Card>

          {/* Host Info */}
          <Card className="mb-6">
            <CardContent className="flex items-center gap-4 py-6">
              <div className="h-16 w-16 shrink-0 rounded-full bg-primary/10 flex items-center justify-center">
                <Building2 className="h-8 w-8 text-primary" />
              </div>
              <div className="min-w-0">
                {workspace.ownerDisplayName ? (
                  <>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Propietario</p>
                    <h3 className="font-semibold text-lg break-words">{workspace.ownerDisplayName}</h3>
                  </>
                ) : (
                  <h3 className="font-semibold text-lg">Propietario</h3>
                )}
                <p className="text-sm text-muted-foreground">
                  Espacio gestionado desde {new Date(workspace.createdAt).getFullYear()}
                </p>
                <p className="text-sm text-muted-foreground">
                  {workspace.city}, {workspace.countryCode}
                </p>
              </div>
            </CardContent>
          </Card>

          <SimilarWorkspaces workspaceId={workspace.id} />
        </div>
      </div>
    </div>
  );
}

function SimilarWorkspaces({ workspaceId }: { workspaceId: string }) {
  const { data: similars } = useQuery<Array<{
    id: string;
    title: { es?: string; en?: string };
    slug: string;
    citySlug: string | null;
    priceMin: number | null;
    priceMax: number | null;
    image: string | null;
    category?: { es?: string; en?: string };
    categorySlug?: string;
  }>>({
    queryKey: ['similar-workspaces', workspaceId],
  });

  if (!similars || similars.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <h3 className="font-semibold text-lg">Espacios similares</h3>
      </CardHeader>
      <CardContent className="space-y-4">
        {similars.map((space) => {
          const url = urlPattern.build({
            city: space.citySlug || undefined,
            category: space.categorySlug,
            space: space.slug,
          });
          return (
            <Link key={space.id} to={url} className="block group">
              <div className="flex gap-3">
                {space.image && (
                  <div className="w-20 h-16 rounded-md overflow-hidden flex-shrink-0">
                    <img
                      src={getImageUrl(space.image, '150x120')}
                      alt={space.title?.es || space.title?.en || ''}
                      className="object-cover w-full h-full group-hover:scale-105 transition-transform"
                    />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate group-hover:text-primary transition-colors">
                    {space.title?.es || space.title?.en}
                  </p>
                  {space.category && (
                    <p className="text-xs text-muted-foreground">
                      {space.category.es || space.category.en}
                    </p>
                  )}
                  {(space.priceMin || space.priceMax) && (
                    <p className="text-xs font-medium text-primary">
                      {space.priceMin && `${space.priceMin}€`}
                      {space.priceMin && space.priceMax && ' - '}
                      {space.priceMax && `${space.priceMax}€`}
                    </p>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
      </CardContent>
    </Card>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();
  const { city, category } = useParams();

  const isRouteError404 = isRouteErrorResponse(error) && error.status === 404;

  if (isRouteError404) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h1 className="text-4xl font-bold mb-4">Espacio no encontrado</h1>
        <p className="text-muted-foreground mb-8">
          Lo sentimos, el espacio que buscas no existe o ha sido eliminado.
        </p>
        <div className="flex gap-4 justify-center">
          <Button asChild>
            <Link to={urlPattern.build({ city, category })}>
              Ver otros espacios
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/">
              Ir al inicio
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  // For other errors, show a generic message
  return (
    <div className="container mx-auto px-4 py-16 text-center">
      <h1 className="text-4xl font-bold mb-4">Algo salió mal</h1>
      <p className="text-muted-foreground mb-8">
        Ha ocurrido un error inesperado. Por favor, inténtalo de nuevo.
      </p>
      <Button asChild>
        <Link to="/">
          Ir al inicio
        </Link>
      </Button>
    </div>
  );
}
